import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  PlayerStatus,
  SmartTuneState,
  Station,
  ConsentState,
} from '@/types/domain';

interface PlayerState {
  status: PlayerStatus;
  currentStation: Station | null;
  currentStreamIndex: number;
  isMuted: boolean;
  volume: number;
  errorMessage: string | null;
  // previously manually played station (for "previous" action)
  previousStation: Station | null;
  // selection source of the current station
  selectionSource: 'manual' | 'smart_tune' | 'search' | 'favorite' | null;
  // Smart Tune
  smartTune: SmartTuneState;
  smartTuneMessage: string | null;

  // actions
  loadStation: (
    station: Station,
    source: 'manual' | 'smart_tune' | 'search' | 'favorite',
  ) => void;
  setStatus: (status: PlayerStatus) => void;
  setError: (message: string | null) => void;
  play: () => void;
  pause: () => void;
  toggleMute: () => void;
  setVolume: (v: number) => void;
  retry: () => void;
  playPrevious: () => void;
  setSmartTune: (state: SmartTuneState) => void;
  setSmartTuneMessage: (msg: string | null) => void;
}

export const usePlayerStore = create<PlayerState>()(
  persist(
    (set, get) => ({
      status: 'idle',
      currentStation: null,
      currentStreamIndex: 0,
      isMuted: false,
      volume: 0.8,
      errorMessage: null,
      previousStation: null,
      selectionSource: null,
      smartTune: 'disabled',
      smartTuneMessage: null,

      loadStation: (station, source) => {
        const prev = get().currentStation;
        // only remember as "previous" if it was a manual context switch
        const prevToStore =
          prev && prev.id !== station.id && get().selectionSource === 'manual'
            ? prev
            : get().previousStation;
        set({
          currentStation: station,
          currentStreamIndex: 0,
          status: 'loading',
          errorMessage: null,
          previousStation: prevToStore,
          selectionSource: source,
        });
      },
      setStatus: (status) => set({ status }),
      setError: (message) =>
        set({ errorMessage: message, status: message ? 'error' : get().status }),
      play: () => set({ status: 'playing', errorMessage: null }),
      pause: () => set({ status: 'paused' }),
      toggleMute: () => set((s) => ({ isMuted: !s.isMuted })),
      setVolume: (v) => set({ volume: Math.max(0, Math.min(1, v)) }),
      retry: () => {
        const st = get().currentStation;
        if (st) {
          set({ status: 'loading', errorMessage: null });
        }
      },
      playPrevious: () => {
        const prev = get().previousStation;
        if (prev) {
          set({
            currentStation: prev,
            currentStreamIndex: 0,
            status: 'loading',
            errorMessage: null,
            selectionSource: 'manual',
          });
        }
      },
      setSmartTune: (smartTune) => set({ smartTune }),
      setSmartTuneMessage: (smartTuneMessage) => set({ smartTuneMessage }),
    }),
    {
      name: 'radioatlas-player',
      // only persist lightweight prefs, not transient status
      partialize: (s) => ({
        volume: s.volume,
        isMuted: s.isMuted,
        smartTune: s.smartTune,
        currentStation: s.currentStation,
      }),
    },
  ),
);

// Consent store — saved per-browser.
interface ConsentStore {
  consent: ConsentState;
  grant: () => void;
  deny: () => void;
  reset: () => void;
}

export const useConsentStore = create<ConsentStore>()(
  persist(
    (set) => ({
      consent: 'not_given',
      grant: () => set({ consent: 'granted' }),
      deny: () => set({ consent: 'denied' }),
      reset: () => set({ consent: 'not_given' }),
    }),
    { name: 'radioatlas-consent' },
  ),
);
